import React, { useContext } from "react";
// Libraries
import { Redirect, useHistory, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import MaskedInput from "react-text-mask";
import * as Yup from "yup";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
// API
import { GetContactById, GetContactById_contact, GetContactByIdVariables } from "api/AddressBook/types/GetContactById";
import { GET_ADDRESS_BOOK_LIST, GET_ADDRESS_BY_ID } from "api/AddressBook/queries";
import { CreateContact, CreateContactVariables } from "api/AddressBook/types/CreateContact";
import { UpdateContact, UpdateContactVariables } from "api/AddressBook/types/UpdateContact";
import { CREATE_CONTACT, UPDATE_CONTACT } from "api/AddressBook/mutations";
import { GetContactList, GetContactListVariables } from "api/AddressBook/types/GetContactList";
import { ContactContactType } from "api/graphql-global-types";
// Utils
import { useBreakpoints, useEventRequest, useGetCountries } from "hooks";
import { CountriesCodes } from "hooks/useGetCurrentBusinessUnit";
import { ADDRESS_BOOK, CREATE_ADDRESS, SETTINGS } from "constants/Routes";
import { getBUFromLocalStorage } from "helpers/storage";
import { handleEnterPress, handleErrorCheck } from "helpers/general";
import { cphNumber, cphNumberMask } from "helpers/regex";
import { CommonContext } from "config/commonProvider";
// Components
import {
  Breadcrumbs,
  Button,
  Field,
  FieldSelect,
  FieldText,
  Flex,
  FormBuilder,
  Header,
  PageContent,
  PageHeader,
  Panel,
  Spacer,
  Text,
} from "components";
import { BaseInput } from "components/Common/Field/BaseInput";
// Styles
import styles from "./styles.module.scss";

const validationSchemaContactRequest = Yup.object().shape(
  {
    name: Yup.string().required("Contact name is required!"),
    contactType: Yup.string().required("Contact type is required!"),
    farmId: Yup.string().when(["contactType", "addressLine1", "country"], {
      is: (contactType, addressLine1, country) => {
        return contactType === ContactContactType.FARM && (!addressLine1 || !country);
      },
      then: Yup.string()
        .required(" ")
        .when("countryCode", {
          is: (countryCode) => countryCode === CountriesCodes.GB,
          then: Yup.string().matches(cphNumber, " "),
        }),
      otherwise: Yup.string(),
    }),
    addressLine1: Yup.string().when(["contactType", "farmId"], {
      is: (contactType, farmId) => {
        return contactType === ContactContactType.FARM && !farmId;
      },
      then: Yup.string().required(" "),
      otherwise: Yup.string(),
    }),
    country: Yup.string().when(["contactType", "farmId"], {
      is: (contactType, farmId) => {
        return contactType === ContactContactType.FARM && !farmId;
      },
      then: Yup.string().required(" "),
      otherwise: Yup.string(),
    }),
    email: Yup.string().email("E-mail is not valid!"),
  },
  [
    ["farmId", "addressLine1"],
    ["country", "farmId"],
  ],
);

const radioButtonClasses = { root: styles.root, checked: styles.checked };

const CreateEditContactAddress: React.FC = () => {
  const { id } = useParams<SettingsAddressEditPageParams>();
  const history = useHistory();
  const { eventsRequest } = useEventRequest();
  const { isBreakpointL } = useBreakpoints();
  const { data: countries } = useGetCountries();
  const { showNotification } = useContext(CommonContext);

  const [createContact, { loading: createContactLoading }] = useMutation<CreateContact, CreateContactVariables>(
    CREATE_CONTACT,
  );
  const [updateContact, { loading: updateContactLoading }] = useMutation<UpdateContact, UpdateContactVariables>(
    UPDATE_CONTACT,
  );

  const isLoading = createContactLoading || updateContactLoading;

  const filteredCountries = countries?.countries?.map((country) => {
    return {
      key: country?.id,
      label: country?.name,
      value: country?.id,
    };
  });
  const isCreateMode = id === CREATE_ADDRESS.substring(1);
  const addressBookRoute = `${SETTINGS}${ADDRESS_BOOK}`;

  const { data, error } = useQuery<GetContactById, GetContactByIdVariables>(GET_ADDRESS_BY_ID, {
    variables: {
      businessUnitId: getBUFromLocalStorage(),
      id: +(id || 0),
    },
    skip: isCreateMode,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });

  const contact = data?.contact as GetContactById_contact;

  const title: string = isCreateMode ? "Add a contact" : "Edit a contact";

  if (error) {
    return <Redirect to={addressBookRoute} />;
  }

  const saveContact = async (props): Promise<void> => {
    const businessUnit = getBUFromLocalStorage();
    const input = {
      ...props,
      businessUnit,
      country: props.country || null,
    };
    delete input.countryCode;
    try {
      let success: boolean | null | undefined = null;

      if (!isCreateMode) {
        const { data } = await updateContact({
          variables: {
            input: {
              ...input,
              id,
            },
          },
        });
        success = !data?.updateContact?.errors;
      } else {
        const { data } = await createContact({
          variables: {
            input,
          },
          update: (cache, { data }) => {
            if (data?.createContact?.contact) {
              const previousData = cache.readQuery<GetContactList, GetContactListVariables>({
                query: GET_ADDRESS_BOOK_LIST,
                variables: {
                  businessUnit,
                },
              });

              const updatedContactsList = [...(previousData?.contacts || []), data?.createContact?.contact];

              cache.writeQuery({
                query: GET_ADDRESS_BOOK_LIST,
                variables: {
                  businessUnit,
                },
                data: {
                  contacts: updatedContactsList,
                },
              });
            }
          },
        });
        success = !data?.createContact?.errors;
        if (success) {
          eventsRequest({
            name: "settings-create-contact-in-address-boook",
            details: { contactId: data?.createContact?.contact?.id },
          });
        }
      }

      if (!success) {
        showNotification({
          variant: "error",
          message: `Error ${isCreateMode ? "creating" : "updating"} contact`,
        });
      } else {
        showNotification({
          message: `Contact successfully ${isCreateMode ? "created" : "updated"}`,
        });
        history.push(`${SETTINGS}${ADDRESS_BOOK}`);
      }
    } catch (e) {
      showNotification({
        variant: "error",
        message: "Something went wrong! Please try again later.",
      });
    }
  };

  const handleCancel = (): void => {
    history.push(`${SETTINGS}${ADDRESS_BOOK}`);
  };

  const initialValues = {
    name: contact?.name || "",
    organisationName: contact?.organisationName || "",
    addressLine1: contact?.addressLine1 || "",
    addressLine2: contact?.addressLine2 || "",
    farmId: contact?.farmId || "",
    city: contact?.city || "",
    country: contact?.country?.id || "",
    postcode: contact?.postcode || "",
    countryCode: contact?.country?.code || "",
    phoneNumber: contact?.phoneNumber || "",
    email: contact?.email || "",
    contactType: contact?.contactType || ContactContactType.FARM,
  };

  return (
    <>
      <PageHeader>
        <Breadcrumbs
          rootPaths={[
            {
              path: "Address book",
              route: addressBookRoute,
            },
          ]}
          currentPath={title}
        />
        <Header backButton title={title} />
      </PageHeader>

      <PageContent>
        <Spacer baselineHeight={3} />
        <FormBuilder
          enableReinitialize
          initialValues={initialValues}
          onSubmit={saveContact}
          validationSchema={validationSchemaContactRequest}
        >
          {({ values, errors, handleChange, handleSubmit, setFieldValue, setFieldError }): JSX.Element => {
            const handleContactTypeChange = (e: React.ChangeEvent<unknown>, value: string): void => {
              setFieldValue("contactType", value);
              setFieldError("addressLine1", null);
              setFieldError("country", null);
              setFieldError("farmId", null);
            };
            const handleRegionChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
              const countryCode = countries?.countries
                ?.find((item) => item?.id === e.target.value)
                ?.code?.toLowerCase();
              setFieldValue("country", e.target.value);
              setFieldValue("countryCode", countryCode);
              setFieldValue("farmId", "");
            };
            const isUK = values.countryCode.toLowerCase() === CountriesCodes.GB;
            const isFarm = values.contactType === ContactContactType.FARM;
            return (
              <Panel>
                <Flex container>
                  <Flex item xs={12} l={6} xl={4}>
                    <Field error={error} labelFor="contactType" label="Contact Type">
                      <RadioGroup
                        aria-label="Contact type"
                        name="contactType"
                        value={values.contactType}
                        onChange={handleContactTypeChange}
                        row
                      >
                        <FormControlLabel
                          value={ContactContactType.FARM}
                          control={<Radio classes={radioButtonClasses} />}
                          label="Farm"
                        />
                        <FormControlLabel
                          value={ContactContactType.OTHER}
                          control={<Radio classes={radioButtonClasses} />}
                          label="Other"
                        />
                      </RadioGroup>
                    </Field>
                    {!isBreakpointL && <Spacer baselineHeight={1} />}
                    <FieldText
                      label="Contact Name"
                      error={handleErrorCheck(errors.name)}
                      inputProps={{
                        name: "name",
                        value: values.name,
                        autoFocus: true,
                        onChange: handleChange,
                        onKeyPress: (e): void => handleEnterPress(e, handleSubmit),
                      }}
                    />
                    {!isBreakpointL && <Spacer baselineHeight={1} />}
                    <FieldSelect
                      label="Country"
                      error={handleErrorCheck(errors.country)}
                      inputProps={{
                        name: "country",
                        value: values.country,
                        options: filteredCountries,
                        onChange: handleRegionChange,
                      }}
                    />
                    {!isBreakpointL && <Spacer baselineHeight={1} />}
                    <FieldText
                      label="Organisation/Holding Name"
                      error={handleErrorCheck(errors.organisationName)}
                      inputProps={{
                        name: "organisationName",
                        value: values.organisationName,
                        onChange: handleChange,
                        onKeyPress: (e): void => handleEnterPress(e, handleSubmit),
                      }}
                    />
                    {!isBreakpointL && <Spacer baselineHeight={1} />}
                    {isFarm && (
                      <>
                        {isUK ? (
                          <MaskedInput
                            label={"County Parish Holding (CPH) Number"}
                            mask={cphNumberMask}
                            name="farmId"
                            value={values.farmId}
                            onChange={handleChange}
                            render={(ref, props): JSX.Element => (
                              <Field error={errors.farmId} labelFor="farmId" {...props}>
                                <BaseInput hasError={!!errors.farmId} innerRef={ref} {...props} />
                              </Field>
                            )}
                          />
                        ) : (
                          <FieldText
                            label={"County Parish Holding (CPH) Number"}
                            error={handleErrorCheck(errors.farmId)}
                            inputProps={{
                              name: "farmId",
                              value: values.farmId,
                              onChange: handleChange,
                              onKeyPress: (e): void => handleEnterPress(e, handleSubmit),
                            }}
                          />
                        )}
                        {!isBreakpointL && <Spacer baselineHeight={1} />}
                      </>
                    )}
                    <FieldText
                      label="Enter address line 1"
                      error={handleErrorCheck(errors.addressLine1)}
                      inputProps={{
                        name: "addressLine1",
                        value: values.addressLine1,
                        onChange: handleChange,
                        onKeyPress: (e): void => handleEnterPress(e, handleSubmit),
                      }}
                    />
                    {!isBreakpointL && <Spacer baselineHeight={1} />}
                    <FieldText
                      label="Enter address line 2"
                      error={handleErrorCheck(errors.addressLine2)}
                      inputProps={{
                        name: "addressLine2",
                        value: values.addressLine2,
                        onChange: handleChange,
                        onKeyPress: (e): void => handleEnterPress(e, handleSubmit),
                      }}
                    />
                    {!isBreakpointL && <Spacer baselineHeight={1} />}
                    <FieldText
                      label="Town/City"
                      error={handleErrorCheck(errors.city)}
                      inputProps={{
                        name: "city",
                        value: values.city,
                        onChange: handleChange,
                        onKeyPress: (e): void => handleEnterPress(e, handleSubmit),
                      }}
                    />
                    {!isBreakpointL && <Spacer baselineHeight={1} />}
                    <FieldText
                      label="Postcode"
                      error={handleErrorCheck(errors.postcode)}
                      inputProps={{
                        name: "postcode",
                        value: values.postcode,
                        onChange: handleChange,
                        onKeyPress: (e): void => handleEnterPress(e, handleSubmit),
                      }}
                    />
                    {!isBreakpointL && <Spacer baselineHeight={1} />}
                    <FieldText
                      label="Phone Number"
                      error={handleErrorCheck(errors.phoneNumber)}
                      inputProps={{
                        name: "phoneNumber",
                        value: values.phoneNumber,
                        onChange: handleChange,
                        onKeyPress: (e): void => handleEnterPress(e, handleSubmit),
                      }}
                    />
                    {!isBreakpointL && <Spacer baselineHeight={1} />}
                    <FieldText
                      label="Email"
                      error={handleErrorCheck(errors.email)}
                      inputProps={{
                        name: "email",
                        value: values.email,
                        onChange: handleChange,
                        onKeyPress: (e): void => handleEnterPress(e, handleSubmit),
                      }}
                    />
                    {isFarm && errors.farmId && errors.addressLine1 && (
                      <Text smallest warning>
                        A CPH number or address is required
                      </Text>
                    )}
                    <Spacer baselineHeight={2} />
                    <Flex container>
                      <Flex item>
                        <Button caption="Cancel" colour="grey" variant="hollow" onClick={handleCancel} />
                      </Flex>
                      <Flex item itemGutter>
                        <Button
                          caption="Save & Continue"
                          onClick={handleSubmit}
                          requesting={isLoading}
                          disabled={isLoading}
                        />
                      </Flex>
                    </Flex>
                    {!isBreakpointL && <Spacer baselineHeight={1} />}
                  </Flex>
                </Flex>
              </Panel>
            );
          }}
        </FormBuilder>
      </PageContent>
    </>
  );
};

export default CreateEditContactAddress;
